import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { UsersRound } from "lucide-react";

import { Card, CardContent, Skeleton } from "@kuzco/web/ui";

import { MembersTable } from "../../../components/admin/members/MembersTable";
import { MembersToolbar } from "../../../components/admin/members/MembersToolbar";
import { LeaderboardPagination } from "../../../components/leaderboard/LeaderboardPagination";
import { nubankTrpc } from "../../../trpc";

export const Route = createFileRoute("/_authed/admin/members/")({
  component: MembersPage,
  head: () => ({
    meta: [{ title: "Members | NBDC" }],
  }),
});

function MembersPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  const query = nubankTrpc.admin.members.list.useQuery({
    search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    page,
    pageSize,
  });

  const members = query.data?.members ?? [];
  const totalCount = query.data?.totalCount ?? 0;
  const totalPages = query.data?.totalPages ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <UsersRound className="h-6 w-6 text-muted-foreground" />
        <h1 className="text-3xl font-semibold tracking-tight">Members</h1>
      </div>

      <MembersToolbar searchValue={search} onSearchChange={setSearch} />

      <Card>
        <CardContent className="p-0">
          {query.isPending ? (
            <div className="flex flex-col gap-2 p-6">
              {Array.from({ length: pageSize }).map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))}
            </div>
          ) : query.isError ? (
            <p className="p-6 text-sm text-detail-failure">
              {query.error.message}
            </p>
          ) : members.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              {debouncedSearch.length > 0
                ? "No members match this search."
                : "No members in this organization yet."}
            </p>
          ) : (
            <MembersTable entries={members} />
          )}
        </CardContent>
      </Card>

      <LeaderboardPagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalUsers={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
      />
    </div>
  );
}
